import AsyncStorage from "@react-native-async-storage/async-storage";
import { useEffect, useState } from "react";

import { getConfig } from "@/getConfig";

import fallbackJson from "../../../public/c/3dfa215eb62b85d86162f2c9206913ea/contacts.json";

/** Do not rotate — native apps embed this path until the next store release. */
export const CONTACT_INFO_JSON_PATH =
    "/c/3dfa215eb62b85d86162f2c9206913ea/contacts.json";

const STORAGE_KEY = "contactInfo.v1";

export type LocalizedText = { en: string; cs: string };

export type ContactPerson = {
    name: string;
    phone?: string;
    email?: string;
};

export type ContactWebsite = {
    label?: string;
    url: string;
};

export type ContactGroup = {
    title: LocalizedText;
    people: ContactPerson[];
};

export type CampusContact = {
    id: string;
    name: LocalizedText;
    address: string;
    officeHours?: string;
    imageUrl?: string;
    website?: ContactWebsite;
    groups: ContactGroup[];
};

export type ContactInfo = {
    version: number;
    updatedAt: string;
    general: {
        email: string;
        phone: string;
        website?: ContactWebsite;
    };
    campuses: CampusContact[];
};

export type ContactInfoStorage = {
    getItem(key: string): Promise<string | null>;
    setItem(key: string, value: string): Promise<void>;
};

export function isContactInfo(value: unknown): value is ContactInfo {
    if (!value || typeof value !== "object") {
        return false;
    }
    const data = value as ContactInfo;
    return (
        data.version === 1 &&
        !!data.general &&
        Array.isArray(data.campuses) &&
        data.campuses.length > 0
    );
}

if (!isContactInfo(fallbackJson)) {
    throw new Error("Bundled contacts.json is invalid.");
}

export const FALLBACK_CONTACT_INFO: ContactInfo = fallbackJson;

export function formatPhoneDisplay(phone: string): string {
    const digits = phone.replace(/\D/g, "");
    if (digits.startsWith("420") && digits.length === 12) {
        return `+420 ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
    }
    return phone;
}

export function localizedText(text: LocalizedText, language: string): string {
    const code = language.startsWith("cs") ? "cs" : "en";
    return text[code] || text.en;
}

export function resolveContactAssetUrl(pathOrUrl: string): string {
    if (/^https?:\/\//i.test(pathOrUrl)) {
        return pathOrUrl;
    }
    const parentWebUrl = getConfig("parentWebUrl");
    const path = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
    return `${parentWebUrl ?? ""}${path}`;
}

type CachedContactInfo = {
    data: ContactInfo;
    etag?: string;
};

function readCached(raw: string | null): CachedContactInfo | null {
    if (!raw) {
        return null;
    }
    try {
        const parsed: unknown = JSON.parse(raw);
        if (
            parsed &&
            typeof parsed === "object" &&
            isContactInfo((parsed as CachedContactInfo).data)
        ) {
            const etag = (parsed as CachedContactInfo).etag;
            return {
                data: (parsed as CachedContactInfo).data,
                etag: typeof etag === "string" ? etag : undefined,
            };
        }
        return null;
    } catch {
        return null;
    }
}

export async function loadContactInfo(
    options: {
        fetchImpl?: typeof fetch;
        storage?: ContactInfoStorage;
        url?: string;
    } = {},
): Promise<ContactInfo> {
    const fetchImpl = options.fetchImpl ?? fetch;
    const storage = options.storage ?? AsyncStorage;
    const cached = readCached(await storage.getItem(STORAGE_KEY));

    try {
        const headers: Record<string, string> = { Accept: "application/json" };
        if (cached?.etag) {
            headers["If-None-Match"] = cached.etag;
        }
        const response = await fetchImpl(
            options.url ?? resolveContactAssetUrl(CONTACT_INFO_JSON_PATH),
            { headers },
        );
        if (response.status === 304 && cached) {
            return cached.data;
        }
        if (!response.ok) {
            return cached?.data ?? FALLBACK_CONTACT_INFO;
        }
        const data: unknown = await response.json();
        if (!isContactInfo(data)) {
            return cached?.data ?? FALLBACK_CONTACT_INFO;
        }
        const etag = response.headers.get("etag") ?? undefined;
        await storage.setItem(STORAGE_KEY, JSON.stringify({ data, etag }));
        return data;
    } catch {
        return cached?.data ?? FALLBACK_CONTACT_INFO;
    }
}

export function useContactInfo(): ContactInfo {
    const [info, setInfo] = useState(FALLBACK_CONTACT_INFO);

    useEffect(() => {
        let cancelled = false;
        void loadContactInfo().then((next) => {
            if (!cancelled) {
                setInfo(next);
            }
        });
        return () => {
            cancelled = true;
        };
    }, []);

    return info;
}
